import { useRef } from 'react';
import FAQSection from '../sections/FAQ';
import { ContactSection } from '../sections/Contact';
import AboutSection from '../sections/About';
import { Services } from '../sections/Service';
import { Hero } from '../sections/Hero';
import { useTranslations } from '../context/TranslationContext';
import { InlineLanguageSwitch } from '../components/LanguageDropDown';
import { Link as RouterLink } from 'react-router-dom';
import useAnalytics from '../hooks/useAnalytics';
import { useReveal } from '../hooks/useReveal';
import { Header } from '../components/Header';
import ArticlesPreview from '../sections/ArticlesPreview';
import { SeoHead } from '../components/SeoHead';
import { toArticleLang } from '../services/articles';
import {
  getHomeMeta,
  homePath,
  buildLegalServiceSchema,
  buildFaqPageSchema,
  buildBreadcrumbSchema,
} from '../services/seo';

const Website = () => {
  const { t, lang } = useTranslations();
  const seoLang = toArticleLang(lang);
  const { trackEvent } = useAnalytics();
  const mainRef = useRef<HTMLDivElement>(null);

  useReveal(mainRef);

  const { title, description } = getHomeMeta(seoLang, t);

  const handleSocialClick = (platform: string, url: string) => {
    trackEvent('social_click', {
      platform,
      url,
      language: lang,
      timestamp: new Date().toISOString()
    });
  };

  const privacyPolicyUrl = `/${lang}/privacy-policy`;

  return (
    <div className="min-h-screen bg-cream" ref={mainRef}>
      <SeoHead
        lang={seoLang}
        title={title}
        description={description}
        path={homePath(seoLang)}
        alternates={{ ro: homePath('ro'), hu: homePath('hu'), en: homePath('en') }}
        jsonLd={[
          buildLegalServiceSchema(seoLang),
          buildFaqPageSchema(seoLang, t),
          buildBreadcrumbSchema([{ name: t.page.navigation.home, path: homePath(seoLang) }]),
        ]}
      />
      <Header variant="home" />

      <Hero />
      <Services />
      <FAQSection />
      <AboutSection />
      <ArticlesPreview />
      <ContactSection />

      <footer className="border-t border-hair py-16 bg-cream">
        <div className="max-w-7xl mx-auto px-8 lg:px-16 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 border border-ink flex items-center justify-center text-[19px] leading-none tracking-[0] font-medium font-serif italic">
              <span className="-translate-x-px -translate-y-px">KT</span>
            </div>
            <span className="text-[11px] tracking-[0.25em] uppercase text-mute">
              &copy; {new Date().getFullYear()} {t.page.title}
            </span>
          </div>
          <div className="flex items-center gap-8">
            <a
              href="https://www.facebook.com/profile.php?id=61571397660008"
              className="link-grow text-[11px] tracking-[0.2em] uppercase text-mute"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleSocialClick('facebook', 'https://www.facebook.com/profile.php?id=61571397660008')}
            >
              Facebook
            </a>
            <a
              href="https://www.linkedin.com/in/kovacs-timea-638838170"
              className="link-grow text-[11px] tracking-[0.2em] uppercase text-mute"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleSocialClick('linkedin', 'https://www.linkedin.com/in/kovacs-timea-638838170')}
            >
              LinkedIn
            </a>
            {/* Public directory profile confirming Baroul Sălaj membership */}
            <a
              href="https://cautavocat.ro/avocat/kovacs-timea-1"
              className="link-grow text-[11px] tracking-[0.2em] uppercase text-mute"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleSocialClick('cautavocat', 'https://cautavocat.ro/avocat/kovacs-timea-1')}
            >
              {t.page.footer.socialLinks.directory}
            </a>
            <RouterLink
              to={privacyPolicyUrl}
              className="link-grow text-[11px] tracking-[0.2em] uppercase text-mute"
              onClick={() => trackEvent('privacy_policy_click', { language: lang, timestamp: new Date().toISOString() })}
            >
              {t.page.footer.privacy.title}
            </RouterLink>
            <InlineLanguageSwitch />
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Website;
